import { useEffect, useMemo, useState, useRef } from 'react';
import { Container } from '@/components/container';
import { DataGrid } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetRevenueReportsQuery,
  useLazyGetRevenueReportsCsvQuery,
} from '@/services/RevenueReportsService';
import { ApexOptions } from 'apexcharts';
import ApexChart from 'react-apexcharts';
import { GroupByUnit, SubscriptionType } from '@/constant';
import Select, { MultiValue } from 'react-select';
import { useSnackbar } from 'notistack';
import { RevenueFilters } from '@/types/revenue-reports.types';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { generateReportPdf } from '@/utils/pdfExport';
import type { PaginationState } from '@tanstack/react-table';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDate, formatDisplayDateRange } from '@/utils/Date';

type SelectOption = { value: string; label: string };
const stripHtml = (input: string) => input.replace(/<[^>]*>/g, '').trim();

const RevenueReport = () => {
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const chartRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const groupByOptions: SelectOption[] = [
    { value: GroupByUnit.DAY, label: 'Daily' },
    { value: GroupByUnit.WEEK, label: 'Weekly' },
    { value: GroupByUnit.MONTH, label: 'Monthly' },
  ];
  const subscriptionTypeOptions: SelectOption[] = [
    { value: String(SubscriptionType.BOATER), label: 'Boater' },
    { value: String(SubscriptionType.HARBOR), label: 'Harbor' },
    { value: String(SubscriptionType.CLUB), label: 'Club' },
    { value: String(SubscriptionType.DMO), label: 'DMO' },
    {
      value: String(SubscriptionType.SERVICE_PROVIDER),
      label: 'Service Provider',
    },
  ];

  const [selectedGroupBy, setSelectedGroupBy] = useState<SelectOption | null>(
    groupByOptions[2]
  );
  const [selectedSubscriptionTypes, setSelectedSubscriptionTypes] = useState<
    SelectOption[]
  >([]);
  const [selectedBillingPeriods] = useState<SelectOption[]>([]);
  const [subscriptionOptions, setSubscriptionOptions] = useState<
    SelectOption[]
  >([]);
  const [selectedSubs, setSelectedSubs] = useState<SelectOption[]>([]);
  const [getCsv, { isLoading: csvLoading }] =
    useLazyGetRevenueReportsCsvQuery();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  const handleExportPdf = async () => {
    try {
      setPdfLoading(true);
      const date = new Date().toISOString().slice(0, 10);

      await generateReportPdf({
        filename: `revenue_report_${date}.pdf`,
        title: 'Revenue Report',
        subtitle: `Grouped by ${selectedGroupBy?.label || 'Month'}`,
        chartElement: chartRef.current || undefined,
        tableElement: tableRef.current || undefined,
        orientation: 'landscape',
        tableData: {
          headers: ['Period', 'Net Revenue'],
          rows: (data?.revenue || []).map((r) => [
            r.period_range
              ? formatDisplayDateRange(r.period_range)
              : formatDisplayDate(new Date(r.period)),
            formatFinnishCurrency(r.net_revenue),
          ]),
        },
      });

      enqueueSnackbar('PDF exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const filters: RevenueFilters = useMemo(() => {
    // Default date range for Day/Week: last 30 days
    // Month: current date to 365 days ago
    // Year: start and end of current year
    const now = new Date();
    const formatDate = (d: Date) => d.toISOString().slice(0, 10);

    let from: string | undefined;
    let to: string | undefined;
    const grp = selectedGroupBy?.value;
    const isDayOrWeek = grp === GroupByUnit.DAY || grp === GroupByUnit.WEEK;
    const isYear = (grp as unknown as string) === 'year';
    const isMonth = grp === GroupByUnit.MONTH;

    const hasCustomStart =
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid();
    const hasCustomEnd =
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid();

    if (isDayOrWeek) {
      const defaultFrom = moment().subtract(29, 'days').format('YYYY-MM-DD');
      const defaultTo = formatDate(now);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else if (isYear) {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const endOfYear = new Date(now.getFullYear(), 11, 31);
      const defaultFrom = formatDate(startOfYear);
      const defaultTo = formatDate(endOfYear);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else if (isMonth) {
      // Month grouping: default to current date to 365 days ago
      const defaultFrom = moment().subtract(365, 'days').format('YYYY-MM-DD');
      const defaultTo = formatDate(now);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else {
      if (hasCustomStart && hasCustomEnd) {
        from = moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        to = moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      }
    }

    return {
      groupBy: selectedGroupBy?.value,
      subscription_types: selectedSubscriptionTypes.map((o) => Number(o.value)),
      billing_periods: selectedBillingPeriods.map((o) => Number(o.value)),
      subscription_ids: selectedSubs.length
        ? selectedSubs.map((o) => Number(o.value))
        : undefined,
      from,
      to,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    };
  }, [
    selectedGroupBy,
    selectedSubscriptionTypes,
    selectedBillingPeriods,
    selectedSubs,
    tempStartDate,
    tempEndDate,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const resetFilters = () => {
    setSelectedGroupBy(groupByOptions[2]);
    setSelectedSubscriptionTypes([]);
    setSelectedSubs([]);
    setTempStartDate('');
    setTempEndDate('');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const { data, isLoading, isFetching, error } =
    useGetRevenueReportsQuery(filters);

  // Reset to first page when filters (other than page/limit) change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    selectedGroupBy,
    selectedSubscriptionTypes,
    selectedBillingPeriods,
    selectedSubs,
  ]);

  // Load subscriptions for user-friendly selection
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await SubscriptionsService.getSubscriptions(1, 100);
      if (!mounted) return;
      const opts: SelectOption[] = (res.subscriptions || []).map((s) => ({
        value: String(s.id),
        label: stripHtml(s.name || ''),
      }));
      setSubscriptionOptions(opts);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (error) {
      const err = error as unknown as { data?: unknown; error?: string };
      let message = 'Failed to fetch revenue reports';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const chart = useMemo(() => {
    const periods = Array.from(
      new Set([
        ...(data?.revenue || []).map((r) => r.period),
        ...(data?.discounts || []).map((d) => d.period),
      ])
    ).sort();
    const categories = periods.map((p) => formatDisplayDate(new Date(p)));
    const revenueSeriesData = periods.map((p) =>
      (data?.revenue || [])
        .filter((r) => r.period === p)
        .reduce((sum, r) => sum + (r.net_revenue || 0), 0)
    );
    const discountSeriesData = periods.map((p) =>
      (data?.discounts || [])
        .filter((d) => d.period === p)
        .reduce((sum, d) => sum + (d.total_discount_amount || 0), 0)
    );

    const series: ApexOptions['series'] = [
      { name: 'Net Revenue', data: revenueSeriesData },
      { name: 'Discounts', data: discountSeriesData },
    ];

    const options: ApexOptions = {
      chart: { type: 'area', height: 300, toolbar: { show: false } },
      dataLabels: { enabled: false },
      legend: { show: true },
      stroke: { curve: 'smooth', show: true, width: 3 },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          formatter: (val) => {
            const n = Number(val);
            return Number.isFinite(n) ? n.toFixed(2) : String(val);
          },
          style: { colors: 'var(--tw-gray-500)', fontSize: '12px' },
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => Number(val).toFixed(2),
          style: { colors: 'var(--tw-gray-500)', fontSize: '12px' },
        },
      },
      tooltip: {
        y: {
          formatter: (val) => formatFinnishCurrency(Number(val)),
        },
      },
      fill: { gradient: { opacityFrom: 0.25, opacityTo: 0 } },
      grid: {
        borderColor: 'var(--tw-gray-200)',
        strokeDashArray: 5,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
    };

    return { options, series };
  }, [data]);

  const handleExportCsv = async () => {
    try {
      const blob = await getCsv({ ...filters, isCsv: true }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `revenue_report_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('CSV exported successfully', { variant: 'success' });
    } catch (e) {
      let message = 'Failed to export CSV';
      if (
        e &&
        typeof e === 'object' &&
        'data' in (e as Record<string, unknown>)
      ) {
        const data = (e as { data?: { message?: string } }).data;
        if (data?.message) message = data.message;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const columns = [
    {
      id: 'period',
      header: 'Period',
      accessorFn: (row: { period: string; period_range?: string }) =>
        row.period_range
          ? formatDisplayDateRange(row.period_range)
          : formatDisplayDate(new Date(row.period)),
      enableSorting: false,
    },
    {
      id: 'net_revenue',
      header: 'Net Revenue',
      accessorFn: (row: { net_revenue: number }) =>
        formatFinnishCurrency(row.net_revenue),
      enableSorting: false,
    },
  ];

  return (
    <Container className="revenue-report">
      {hasPermission('subscription_management_read') && (
        <>
          <div className="flex items-start justify-between mb-6">
            <h1
              id="revenue-report-heading"
              className="text-xl font-semibold"
              tabIndex={-1}
            >
              Revenue Report
            </h1>
            <div className="flex flex-wrap gap-3 sm:gap-5 justify-start sm:justify-end">
              <div className="min-w-[160px]">
                <Select
                  classNamePrefix="react-select"
                  placeholder="Group by"
                  value={selectedGroupBy}
                  options={groupByOptions}
                  onChange={(val) => {
                    setSelectedGroupBy(val as SelectOption);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  aria-label="Group by"
                />
              </div>
              <div className="min-w-[220px]">
                <Select
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="Customer Segment"
                  options={subscriptionTypeOptions}
                  value={selectedSubscriptionTypes}
                  onChange={(vals: MultiValue<SelectOption>) =>
                    setSelectedSubscriptionTypes(vals as SelectOption[])
                  }
                  aria-label="Customer Segment"
                />
              </div>
              <div className="min-w-[240px]">
                <Select
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="Filter subscriptions"
                  options={subscriptionOptions}
                  value={selectedSubs}
                  onChange={(vals: MultiValue<SelectOption>) =>
                    setSelectedSubs(vals as SelectOption[])
                  }
                  aria-label="Filter subscriptions"
                />
              </div>
              <div className="flex-shrink-0">
                <DateRangePicker
                  startDateName="from"
                  endDateName="to"
                  isMandatory={false}
                  value={[tempStartDate, tempEndDate]}
                  onChange={([newStartDate, newEndDate]) => {
                    handleDateRangeChange(
                      newStartDate as string,
                      newEndDate as string
                    );
                  }}
                  allowPastDates={true}
                  allowFutureDates={false}
                  className="w-full sm:w-64 h-8 sm:h-10 text-sm"
                />
              </div>
              {(selectedGroupBy?.value !== GroupByUnit.MONTH ||
                selectedSubscriptionTypes.length > 0 ||
                selectedSubs.length > 0 ||
                !!tempStartDate ||
                !!tempEndDate) && (
                <div className="flex items-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-light flex items-center gap-2"
                    onClick={resetFilters}
                    title="Clear filters"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleExportPdf}
                  disabled={pdfLoading}
                >
                  {pdfLoading ? 'Exporting...' : 'Export PDF'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleExportCsv}
                  disabled={csvLoading}
                >
                  {csvLoading ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <div
              className="card-body flex flex-col justify-end items-stretch grow px-3 py-1"
              ref={chartRef}
            >
              <ApexChart
                id="revenue_report_chart"
                options={chart.options}
                series={chart.series}
                type="area"
                width="100%"
                height="300"
              />
            </div>
          </div>

          <div className="card">
            <div className="card-body" ref={tableRef}>
              <DataGrid
                columns={columns}
                data={data?.revenue || []}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  sizes: [10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                  more: false,
                  info: '{from} - {to} of {count}',
                  totalItems:
                    data?.revenuePagination?.totalItems ??
                    (data?.revenue || []).length,
                  totalPages:
                    data?.revenuePagination?.totalPages ??
                    Math.max(
                      1,
                      Math.ceil(
                        ((data?.revenue || []).length || 0) /
                          (pagination.pageSize || 1)
                      )
                    ),
                  setPagination: setPagination,
                }}
                serverSide={!!data?.revenuePagination}
                onFilterReset={resetFilters}
                hasActiveFilters={
                  selectedGroupBy?.value !== GroupByUnit.MONTH ||
                  selectedSubscriptionTypes.length > 0 ||
                  selectedSubs.length > 0 ||
                  !!tempStartDate ||
                  !!tempEndDate
                }
                aria-label="Revenue table"
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default RevenueReport;
